import Prism from 'prismjs';
// https://github.com/FormidableLabs/prism-react-renderer?tab=readme-ov-file#custom-language-support
(typeof global !== 'undefined' ? global : window).Prism = Prism;
import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-tsx';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-markup-templating';
import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-ruby';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-php';
import 'prismjs/components/prism-go';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-swift';
import 'prismjs/components/prism-kotlin';
import 'prismjs/components/prism-xml-doc';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Highlight, RenderProps, Token } from 'prism-react-renderer';

import { CodeBlock } from '@components/patterns/Code/Block';
import {
  getRenderedVariable,
  isCustomVariable,
  processLine,
} from '@components/patterns/Code/process';
import { CodeVariableMap } from '@components/patterns/Code/types';
import { Language } from '@components/patterns/Code/utilities';
import { PreviewMode } from '@components/patterns/Diff/PreviewMode';
import { cn } from '@components/primitives/utilities';

import { light } from './themes';

import css from './styles.module.css';

const MAX_HIGHLIGHT_KB = 12;
const VARIABLE_PATTERN = /\$[A-Z0-9_]+/g;

interface RenderedTokenProps {
  token: Token;
  variables?: CodeVariableMap;
}

function RenderedToken({ token, variables }: RenderedTokenProps) {
  if (variables && isCustomVariable(token)) {
    const key = token.content.slice(1);
    if (key in variables) {
      const variable = variables[key];
      return <>{getRenderedVariable(variable)}</>;
    }
  }

  return token.content;
}

interface RenderedLineProps {
  line: Token[];
  variables?: CodeVariableMap;
  getTokenProps: RenderProps['getTokenProps'];
}

function RenderedLine({ line, getTokenProps, variables }: RenderedLineProps) {
  const processed = useMemo(
    () => processLine({ line, pattern: VARIABLE_PATTERN, variables }),
    [line, variables]
  );

  return (
    <>
      {processed.map((token, index) => (
        <span key={`t-${index}`} {...getTokenProps({ token })}>
          <RenderedToken token={token} variables={variables} />
        </span>
      ))}
    </>
  );
}

interface ScrollableCodeProps extends RenderProps {
  showLineNumbers?: boolean;
  maxLines?: number;
  previewLines?: number;
  previewActive?: boolean;
  onPreviewActiveChange?: (active: boolean) => void;
  variables?: CodeVariableMap;
}

function ScrollableCode({
  tokens,
  style,
  getLineProps,
  getTokenProps,
  className,
  showLineNumbers,
  maxLines = 15,
  previewLines = 0,
  variables,
  ...props
}: ScrollableCodeProps) {
  const lineCount = tokens.length;

  const scrollableRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: lineCount,
    estimateSize: () => 22,
    getScrollElement: () => scrollableRef.current,
    overscan: 50,
  });
  const rows = virtualizer.getVirtualItems();

  const internalState = useState(true);
  const previewActive = props.previewActive ?? internalState[0];
  const setPreviewActive = props.onPreviewActiveChange ?? internalState[1];
  useEffect(() => {
    if (previewActive) {
      virtualizer.scrollToOffset(0, { behavior: 'auto' });
      scrollableRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [previewActive, virtualizer.scrollToOffset]);

  const getLineNumber = useCallback(
    (index: number) => {
      const length = String(lineCount).length;
      return String(index + 1).padStart(length, ' ');
    },
    [tokens]
  );

  return (
    <PreviewMode
      enabled={previewLines > 0 && lineCount > previewLines}
      lineHeight={22}
      lines={previewLines}
      active={previewActive}
      onActiveChange={setPreviewActive}
    >
      <div
        ref={scrollableRef}
        className={cn(css.scrollable, className)}
        style={{ '--max-visible-lines': maxLines }}
      >
        <pre className={css.code} style={style}>
          <code
            className={css.content}
            style={{
              '--code-height': `${virtualizer.getTotalSize()}px`,
              '--code-start': `${rows.at(0)?.start ?? 0}px`,
            }}
          >
            <div className={css.lines}>
              {rows.map((item) => {
                const line = tokens[item.index];
                const props = getLineProps({
                  line,
                  className: css.line,
                  style: {
                    '--line-height': `${item.size}px`,
                    '--line-start': `${item.start - virtualizer.options.scrollMargin}px`,
                  },
                });

                return (
                  <div key={item.key} {...props}>
                    {showLineNumbers && (
                      <span className={css.lineNumber}>
                        <span>
                          {getLineNumber(item.index)}
                          {'   '}
                        </span>
                      </span>
                    )}

                    <RenderedLine
                      line={line}
                      variables={variables}
                      getTokenProps={getTokenProps}
                    />
                  </div>
                );
              })}
            </div>
          </code>
        </pre>
      </div>
    </PreviewMode>
  );
}

interface CodeProps {
  children: string;
  language: Language;
  className?: string;
  /** Whether to show line numbers */
  lineNumbers?: boolean;
  /** The maximum number of lines to display. Default is 15 */
  lines?: number;
  /** The number of lines to display in preview mode. Default is 0 (disabled) */
  previewLines?: number;
  /** Whether the preview mode is active. */
  previewActive?: boolean;
  /** Callback when the preview mode is changed. */
  onPreviewActiveChange?: (active: boolean) => void;
  /** Variables to replace in the code. */
  variables?: CodeVariableMap;
}

function Code({
  children,
  language,
  className,
  lineNumbers,
  lines = 15,
  previewLines = 0,
  previewActive,
  onPreviewActiveChange,
  variables,
}: CodeProps) {
  const isTooLarge = useMemo(
    () => children.length > MAX_HIGHLIGHT_KB * 1024,
    [children]
  );

  return (
    <Highlight
      code={children.trim()}
      theme={light}
      prism={Prism}
      language={isTooLarge ? 'plain' : language}
    >
      {(props) => (
        <ScrollableCode
          {...props}
          className={cn(props.className, className)}
          showLineNumbers={lineNumbers}
          previewLines={previewLines}
          maxLines={lines}
          previewActive={previewActive}
          onPreviewActiveChange={onPreviewActiveChange}
          variables={variables}
        />
      )}
    </Highlight>
  );
}

const CodeNamespace = Object.assign(Code, {
  Block: CodeBlock,
});

export { CodeNamespace as Code };
